import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterListIcon from '@mui/icons-material/FilterList';
import { Box, Button, Collapse, IconButton, TextField, Typography, useMediaQuery } from '@mui/material';
import React from 'react';
import { LayoutRows2 } from '../LayoutRows2';
import { FilterChips, FilterComponent } from './components';
import { getChipLabel } from './components/getChipLabel';
const defaultWidth = 480;
const searchFieldLabel = 'Search';
/**
 * @param {DataFilterProps} props - Props for configuring the filter panel, including group configs, search field, applied values, and callbacks.
 * @returns {React.ReactElement | null} The rendered data filter panel, or null if no groups or search field is present.
 * @deprecated Replaced by `@juvare/common-data-table-ui`.
 */
export function DataFilter(props) {
    var _a, _b;
    const smDownScreen = useMediaQuery((theme) => theme.breakpoints.down('sm'));
    const { fullTextSearchField, onChange, showFullTextSearchChipLabel, fullTextSearchInitialValue, onInitialChipLabel } = props;
    const chipLabel = React.useCallback((value) => {
        return getChipLabel({
            valueLabel: value,
            showLabel: showFullTextSearchChipLabel,
            label: searchFieldLabel,
        });
    }, [showFullTextSearchChipLabel]);
    React.useEffect(() => {
        if (fullTextSearchField && fullTextSearchInitialValue && onInitialChipLabel) {
            onInitialChipLabel(fullTextSearchField, getChipLabel({
                label: searchFieldLabel,
                showLabel: showFullTextSearchChipLabel,
                valueLabel: fullTextSearchInitialValue,
            }));
        }
    }, [fullTextSearchField, fullTextSearchInitialValue, onInitialChipLabel, showFullTextSearchChipLabel]);
    const handleFullTextSearchChange = React.useCallback((value) => {
        if (fullTextSearchField) {
            onChange(fullTextSearchField, value, undefined, undefined, chipLabel(value));
        }
    }, [onChange, fullTextSearchField, chipLabel]);
    const handleSubmit = (e) => {
        e.preventDefault();
        props.onApply();
    };
    const groups = (_a = props.groups) !== null && _a !== void 0 ? _a : [];
    const normalGroups = groups.filter((g) => !g.advanced);
    const advancedGroups = groups.filter((g) => g.advanced);
    const FilterActionsComponent = props.filterActionsComponent;
    const width = smDownScreen ? '100%' : ((_b = props.width) !== null && _b !== void 0 ? _b : defaultWidth);
    return groups.length > 0 || props.fullTextSearchField ? (React.createElement(Box, { sx: {
            display: 'flex',
            flexDirection: 'column',
            boxSizing: 'border-box',
            height: '100%',
            overflow: 'hidden',
            transition: (theme) => theme.transitions.create('width'),
            width: props.open ? width : 0,
        }, "data-test": 'data-filter' },
        React.createElement(Box, { sx: { boxSizing: 'border-box', height: '100%', overflow: 'hidden', width: width } },
            React.createElement(Box, { sx: { height: '100%', overflow: 'auto' } },
                React.createElement("form", { onSubmit: handleSubmit, style: { height: '100%' } },
                    React.createElement(LayoutRows2, { row1: React.createElement(Box, { sx: {
                                display: 'flex',
                                alignItems: 'center',
                                padding: 2,
                                paddingBottom: 1,
                                visibility: props.open ? 'visible' : 'hidden',
                            }, "aria-hidden": !props.open, "data-test": 'toolbar' },
                            React.createElement(Button, { sx: {
                                    marginRight: 2,
                                    '&:last-child': {
                                        marginRight: 0,
                                    },
                                }, variant: 'outlined', color: 'primary', startIcon: React.createElement(FilterListIcon, null), disabled: props.filterChanged, onClick: props.onHide, "data-test": 'filter-button' },
                                "Filter ",
                                props.filterCount > 0 ? '(' + props.filterCount + ')' : ''),
                            FilterActionsComponent && (React.createElement(FilterActionsComponent, { filterChanged: props.filterChanged, filterCount: props.filterCount })),
                            props.filterChanged && (React.createElement(React.Fragment, null,
                                React.createElement(Box, { sx: { flexGrow: 1 } }),
                                React.createElement(Button, { sx: {
                                        marginRight: 2,
                                        '&:last-child': {
                                            marginRight: 0,
                                        },
                                    }, color: 'primary', onClick: props.onCancel, "data-test": 'filter-cancel-button' }, "Cancel"),
                                React.createElement(Button, { sx: {
                                        marginRight: 2,
                                        '&:last-child': {
                                            marginRight: 0,
                                        },
                                    }, type: 'submit', variant: 'contained', color: 'primary', disabled: Object.keys(props.value.error).length > 0, onClick: () => {
                                        props.onApply();
                                        if (smDownScreen) {
                                            props.onHide();
                                        }
                                    }, "data-test": 'filter-apply-button' }, "Apply"))),
                            !props.filterChanged && props.filterCount > 0 && (React.createElement(React.Fragment, null,
                                React.createElement(Box, { sx: { flexGrow: 1 } }),
                                React.createElement(Button, { sx: {
                                        marginRight: 2,
                                        '&:last-child': {
                                            marginRight: 0,
                                        },
                                    }, color: 'primary', onClick: props.onClearAll, "data-test": 'filter-clear-button' }, "Clear All")))), row2: React.createElement(Box, { sx: { visibility: props.open ? 'visible' : 'hidden' }, "aria-hidden": !props.open },
                            props.chipLabelsApplied && props.valueApplied && (React.createElement(FilterChips, { chipLabelsApplied: props.chipLabelsApplied, valueApplied: props.valueApplied })),
                            fullTextSearchField && (React.createElement(FullTextSearch, { value: props.value.value[fullTextSearchField], onChange: handleFullTextSearchChange, isOpen: props.open, searchFieldLabel: searchFieldLabel })),
                            normalGroups.map((group) => (React.createElement(Group, { key: group.label, group: group, value: props.value, onChange: props.onChange, onInitialChipLabel: props.onInitialChipLabel, initialExpandedState: !props.groupsCollapsed, isOpen: props.open }))),
                            advancedGroups.length > 0 && (React.createElement(Advanced, null, advancedGroups.map((group) => (React.createElement(Group, { key: group.label, group: group, value: props.value, onChange: props.onChange, onInitialChipLabel: props.onInitialChipLabel, initialExpandedState: !props.groupsCollapsed, isOpen: props.open })))))), fullHeightRow: 'row2', row2Scrollable: true })))))) : null;
}
export const FullTextSearch = React.memo((props) => {
    var _a;
    const inputRef = React.useRef(undefined);
    React.useEffect(() => {
        if (inputRef.current && props.isOpen) {
            inputRef.current.focus();
        }
    }, [props.isOpen]);
    return (React.createElement(Box, { sx: { padding: 2, borderBottom: (theme) => '1px solid ' + theme.palette.divider }, "data-test": 'full-text-search' },
        React.createElement(TextField, { inputRef: inputRef, fullWidth: true, label: searchFieldLabel, size: 'small', value: (_a = props.value) !== null && _a !== void 0 ? _a : '', onChange: (event) => props.onChange(event.target.value || undefined), "data-test": 'search-input' })));
});
const Group = (props) => {
    const [expanded, setExpanded] = React.useState(props.initialExpandedState);
    return (React.createElement(Box, { sx: { borderBottom: (theme) => '1px solid ' + theme.palette.divider, visibility: props.isOpen ? 'visible' : 'hidden' }, "data-test": 'group' },
        React.createElement(Box, { sx: { display: 'flex', justifyContent: 'space-between' } },
            React.createElement(Typography, { sx: { padding: 2 }, variant: 'body1', component: 'div', "data-test": 'label' }, props.group.label),
            React.createElement(IconButton, { onClick: () => setExpanded(!expanded), size: 'large', "aria-expanded": expanded, "aria-label": props.group.label, "data-test": `${!expanded ? 'expand' : 'collapse'}-button` }, expanded ? React.createElement(ExpandLessIcon, null) : React.createElement(ExpandMoreIcon, null))),
        React.createElement(Collapse, { in: expanded }, props.group.configs.map((config) => (React.createElement(FilterComponent, { key: config.field, config: config, value: props.value.value[config.field], rawStringValue: props.value.rawStringValue[config.field], error: props.value.error[config.field], onChange: props.onChange, onInitialChipLabel: props.onInitialChipLabel }))))));
};
/**
 * Renders a collapsible section for advanced settings or content.
 *
 * @param {React.PropsWithChildren} props - The props object containing optional children elements.
 * @returns {JSX.Element} The rendered advanced section with toggleable visibility.
 */
export const Advanced = (props) => {
    const [expanded, setExpanded] = React.useState(false);
    return (React.createElement("div", null,
        React.createElement(Box, { sx: { padding: 2 }, "data-test": 'advanced' },
            React.createElement(Button, { color: 'primary', onClick: () => setExpanded(!expanded) }, "Advanced")),
        React.createElement(Collapse, { in: expanded, unmountOnExit: true }, props.children)));
};
/**
 * Custom hook that manages the state and behavior of a data filter UI,
 * including value tracking, change detection, chip label management,
 * full-text search integration, and filter application/reset logic.
 * @param {boolean} [open] - Initial open state of the filter panel.
 * @param {DataFilterGroup[]} [groups] - The filter group configurations.
 * @param {string} [fullTextSearchField] - Optional field used for full-text search filtering.
 * @param {string} [fullTextSearchInitialValue] - Optional initial value for the full-text search input.
 * @returns {{
 *   state: DataFilterState;
 *   handleChange: DataFilterProps['onChange'];
 *   handleOpen: () => void;
 *   handleHide: () => void;
 *   handleCancel: () => void;
 *   handleApply: () => void;
 *   handleApplyFullTextQuery: (query: string) => void;
 *   handleClearAll: () => void;
 *   setOpen: (open: boolean) => void;
 *   handleInitialChipLabel: (field: string, chipLabel?: string | string[]) => void;
 * }} Object containing state and action handlers for filter behavior.
 */
export function useDataFilterState(open, groups, fullTextSearchField, fullTextSearchInitialValue) {
    const [state, setState] = React.useState(() => {
        const value = calculateInitialFilterValue(groups, fullTextSearchField, fullTextSearchInitialValue);
        return {
            open: !!open,
            changed: false,
            value: value,
            valueApplied: value,
            filters: mapFilterValueToFieldFilter(value, groups),
            chipLabels: {},
            chipLabelsApplied: {},
        };
    });
    React.useEffect(() => {
        const value = calculateInitialFilterValue(groups, fullTextSearchField, fullTextSearchInitialValue);
        if (Object.keys(value.value).length) {
            setState((state) => (Object.assign(Object.assign({}, state), { changed: false, value: value, valueApplied: value, filters: returnNewFieldFilterIfChanged(state.filters, mapFilterValueToFieldFilter(value, groups)) })));
        }
    }, [groups, fullTextSearchField, fullTextSearchInitialValue]);
    return {
        state: state,
        handleChange: React.useCallback((field, value, rawStringValue, error, chipLabel) => {
            setState((state) => {
                const newValue = {
                    value: Object.assign({}, state.value.value),
                    rawStringValue: Object.assign({}, state.value.rawStringValue),
                    error: Object.assign({}, state.value.error),
                };
                const chipLabels = Object.assign({}, state.chipLabels);
                if (Array.isArray(chipLabel)) {
                    chipLabels[field] = chipLabel;
                }
                else if (chipLabel) {
                    chipLabels[field] = [chipLabel];
                }
                if (value != null) {
                    newValue.value[field] = value;
                }
                else {
                    delete newValue.value[field];
                    delete chipLabels[field];
                }
                if (rawStringValue != null && typeof rawStringValue === 'string') {
                    newValue.rawStringValue[field] = rawStringValue;
                }
                else {
                    delete newValue.rawStringValue[field];
                }
                if (error != null) {
                    newValue.error[field] = error;
                }
                else {
                    delete newValue.error[field];
                }
                return Object.assign(Object.assign({}, state), { changed: true, value: newValue, chipLabels });
            });
        }, []),
        handleOpen: React.useCallback(() => {
            setState((state) => (Object.assign(Object.assign({}, state), { open: true })));
        }, []),
        handleHide: React.useCallback(() => {
            setState((state) => (Object.assign(Object.assign({}, state), { open: false })));
        }, []),
        handleCancel: React.useCallback(() => {
            setState((state) => (Object.assign(Object.assign({}, state), { changed: false, value: state.valueApplied, chipLabels: Object.assign({}, state.chipLabelsApplied) })));
        }, []),
        handleApply: React.useCallback(() => {
            setState((state) => {
                const trimmedValue = trimValue(state.value);
                return Object.assign(Object.assign({}, state), { changed: false, value: trimmedValue, valueApplied: trimmedValue, chipLabelsApplied: Object.assign({}, state.chipLabels), filters: returnNewFieldFilterIfChanged(state.filters, mapFilterValueToFieldFilter(trimmedValue, groups)) });
            });
        }, [groups]),
        handleApplyFullTextQuery: React.useCallback((query) => {
            const value = { value: {}, rawStringValue: {}, error: {} };
            query = query.trim();
            if (fullTextSearchField && query) {
                value.value[fullTextSearchField] = query;
            }
            setState((state) => (Object.assign(Object.assign({}, state), { changed: false, value: value, valueApplied: value, filters: returnNewFieldFilterIfChanged(state.filters, mapFilterValueToFieldFilter(value, groups)) })));
        }, [groups, fullTextSearchField]),
        handleClearAll: React.useCallback(() => {
            const empty = { value: {}, rawStringValue: {}, error: {} };
            setState((state) => (Object.assign(Object.assign({}, state), { changed: false, value: empty, valueApplied: empty, filters: [], chipLabelsApplied: {}, chipLabels: {} })));
        }, []),
        setOpen: React.useCallback((open) => {
            setState((state) => (Object.assign(Object.assign({}, state), { open: open })));
        }, []),
        handleInitialChipLabel: React.useCallback((field, chipLabel) => {
            setState((state) => {
                const chipLabels = Object.assign({}, state.chipLabelsApplied);
                if (Array.isArray(chipLabel)) {
                    chipLabels[field] = chipLabel;
                }
                else if (chipLabel) {
                    chipLabels[field] = [chipLabel];
                }
                return Object.assign(Object.assign({}, state), { chipLabels: Object.assign(Object.assign({}, state.chipLabels), chipLabels), chipLabelsApplied: Object.assign(Object.assign({}, state.chipLabels), chipLabels) });
            });
        }, []),
    };
}
/**
 * Constructs the initial filter value object based on provided filter group configurations
 * and an optional full-text search field and value.
 * @param {DataFilterGroup[]} [groups] - The filter groups with optional initial values.
 * @param {string} [fullTextSearchField] - The field key used for full-text search.
 * @param {string} [fullTextSearchInitialValue] - The initial value for the full-text search field.
 * @returns {FilterValue} The assembled initial filter value, including raw string values and errors.
 */
function calculateInitialFilterValue(groups, fullTextSearchField, fullTextSearchInitialValue) {
    const value = { value: {}, rawStringValue: {}, error: {} };
    if (groups) {
        for (const g of groups) {
            for (const c of g.configs) {
                if (c.initialValue != null) {
                    if (c.type === 'date-from-to') {
                        if (c.initialValue.dateFrom) {
                            c.initialValue.dateFrom.setSeconds(0, 0);
                        }
                        if (c.initialValue.dateTo) {
                            c.initialValue.dateTo.setSeconds(0, 0);
                        }
                    }
                    value.value[c.field] = c.initialValue;
                    if (c.type === 'string') {
                        value.rawStringValue[c.field] =
                            c.type === 'string' && c.formatValue ? c.formatValue(c.initialValue) : c.initialValue;
                    }
                }
            }
        }
    }
    if (fullTextSearchField && fullTextSearchInitialValue) {
        value.value[fullTextSearchField] = fullTextSearchInitialValue;
    }
    return value;
}
/**
 * Converts a filter value object into an array of field filters, optionally using group configuration
 * to assign filter operations.
 * @param {FilterValue} filterValue - The current filter values to convert.
 * @param {DataFilterGroup[]} [groups] - Optional filter groups used to determine filter operations (`op`).
 * @returns {FieldFilter[]} An array of field filters derived from the filter value.
 */
function mapFilterValueToFieldFilter(filterValue, groups) {
    return Object.entries(filterValue.value).map(([field, value]) => {
        let op = undefined;
        if (groups) {
            for (const g of groups) {
                for (const c of g.configs) {
                    if (c.field === field) {
                        op = c.op;
                        break;
                    }
                }
            }
        }
        return {
            field: field,
            value: value,
            op: op,
        };
    });
}
/**
 * Compares two field filter arrays and returns the new one only if it differs from the old one.
 * @param {FieldFilter[]} oldFilter - The previously applied field filters.
 * @param {FieldFilter[]} newFilter - The newly calculated field filters.
 * @returns {FieldFilter[]} The unchanged old filter if identical, or the new filter if changes are detected.
 */
function returnNewFieldFilterIfChanged(oldFilter, newFilter) {
    if (oldFilter.length === newFilter.length) {
        for (let i = 0; i < oldFilter.length; i++) {
            const f1 = oldFilter[i];
            const f2 = newFilter[i];
            if (f1.field !== f2.field || f1.value !== f2.value) {
                return newFilter;
            }
        }
        return oldFilter;
    }
    else {
        return newFilter;
    }
}
/**
 * Returns a new filter value object with all string values trimmed,
 * and removes keys where the trimmed string is empty.
 * @param {FilterValue} value - The original filter value object.
 * @returns {FilterValue} A new filter value with cleaned and trimmed string entries.
 */
function trimValue(value) {
    const trimmedValue = { value: Object.assign({}, value.value), rawStringValue: Object.assign({}, value.rawStringValue), error: Object.assign({}, value.error) };
    for (const [key, val] of Object.entries(value.value)) {
        if (typeof val === 'string') {
            const trimmedString = val.trim();
            if (trimmedString.length > 0) {
                trimmedValue.value[key] = trimmedString;
            }
            else {
                delete trimmedValue.value[key];
            }
        }
    }
    return trimmedValue;
}
